/**
 * Rank by the SHAPE of the org — mirrors the backend's `staffTitles.rankHierarchy`:
 *  - leading anyone at all makes a Manager, however many field agents that is;
 *  - leading TWO OR MORE people who are themselves managers makes a Director;
 *  - nobody may outrank their own boss, so a Director anywhere below makes you one too.
 *
 * Pure and cycle-guarded, because the staff list is whatever the database holds.
 */
export type StaffRank = 'Director' | 'Manager' | null;

export interface RankedPerson {
    directReports: number;
    managerReports: number;
    rank: StaffRank;
}

const NO_TEAM: RankedPerson = { directReports: 0, managerReports: 0, rank: null };

export const rankHierarchy = (
    people: Array<{ id: number; managerId?: number | null }>,
): Map<number, RankedPerson> => {
    const childrenOf = new Map<number, number[]>();
    for (const p of people) {
        if (p.managerId != null) childrenOf.set(p.managerId, [...(childrenOf.get(p.managerId) ?? []), p.id]);
    }

    const out = new Map<number, RankedPerson>();
    const inFlight = new Set<number>();

    const rankOf = (id: number): RankedPerson => {
        const done = out.get(id);
        if (done) return done;
        if (inFlight.has(id)) return NO_TEAM;   // cycle: treat as a leaf rather than recursing
        inFlight.add(id);

        const kids = childrenOf.get(id) ?? [];
        const kidRanks = kids.map(rankOf);
        const managerReports = kidRanks.filter((r) => r.rank !== null).length;
        const directorBelow = kidRanks.some((r) => r.rank === 'Director');

        const result: RankedPerson = {
            directReports: kids.length,
            managerReports,
            rank: managerReports >= 2 || directorBelow ? 'Director' : kids.length >= 1 ? 'Manager' : null,
        };
        inFlight.delete(id);
        out.set(id, result);
        return result;
    };

    for (const p of people) rankOf(p.id);
    return out;
};

/** A FIELD_AGENT's title follows the same rule: Field Manager, then Field Director. */
export const fieldTitleFor = (rank: StaffRank): string | null => (rank ? `Field ${rank}` : null);
